import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import {
  getRuntimeConfig,
  getRuntimeConfigFirst,
} from "../../../../../config/runtimeConfig";

import type {
  ExperimentData,
  ExperimentMetrics,
  ExperimentStatus,
} from "../types/dashboard.types";

const DASHBOARD_QUERY_KEY = ["dashboard-experiments"];
const configuredApiBaseUrl = getRuntimeConfigFirst([
  "VITE_INNOVATION_API_BASE_URL",
  "VITE_API_URL",
]);

const API_BASE_URL_CANDIDATES = [
  configuredApiBaseUrl,
  "https://falcon-api-innovation-dev-ggdcgtajgnhbevb7.centralus-01.azurewebsites.net",
].filter(Boolean) as string[];

const FALLBACK_ORG_ID =
  getRuntimeConfig("VITE_INNOVATION_ORG_ID") ||
  "76c537c7-3890-4e39-9d99-4e8aa8c858ba";

const FALLBACK_USER_ID = "27ad8f65-49c7-4ec7-8d37-79b1326817b3";

type ApiExperiment = {
  id?: string;
  name?: string;
  title?: string;
  goal?: string;
  context?: string;
  description?: string;
  owner?: string;
  type?: number;
  status?: number | string;
  participantsCount?: number | null;
  participantsCompletion?: number | null;
  participantsEvents?: number | null;
  createdAt?: string;
  updatedAt?: string | null;
};

type ExperimentsApiResponse = {
  success?: boolean;
  message?: string;
  data?: {
    experiments?: ApiExperiment[];
  };
};

const fallbackProductPalette = [
  "#2AA9C2",
  "#5074FF",
  "#E74895",
  "#7C8594",
  "#9A4DFF",
  "#0EB3AA",
  "#FF7F50",
  "#61A8FF",
];

const hashString = (value: string) => {
  return value.split("").reduce((hash, character) => {
    return (hash * 31 + character.charCodeAt(0)) >>> 0;
  }, 7);
};

const toInitials = (name: string): string => {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

const toShortName = (name: string): string => {
  return name.split(" ").filter(Boolean)[0] ?? "Owner";
};

const normalizeApiTimestamp = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return null;
  }

  return trimmedValue.includes("T")
    ? trimmedValue
    : trimmedValue.replace(" ", "T");
};

const toValidTimestamp = (value?: string | null): number | null => {
  const normalizedValue = normalizeApiTimestamp(value);
  if (!normalizedValue) {
    return null;
  }

  const timestamp = new Date(normalizedValue).getTime();
  if (Number.isNaN(timestamp)) {
    return null;
  }

  const parsedDate = new Date(timestamp);
  const year = parsedDate.getUTCFullYear();
  if (year < 2020 || year > 2100) {
    return null;
  }

  return timestamp;
};

const getRelativeMinutes = (isoDate?: string | null): number => {
  const timestamp = toValidTimestamp(isoDate);
  if (timestamp === null) {
    return 999999;
  }

  return Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
};

const formatRelativeTime = (isoDate?: string | null): string => {
  const minutes = getRelativeMinutes(isoDate);

  if (minutes === 999999) {
    return "--";
  }

  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? "" : "s"} ago`;
  }

  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
};

const mapApiStatusToDashboardStatus = (
  status: number | string | undefined,
): ExperimentStatus => {
  if (typeof status === "number") {
    switch (status) {
      case 1:
        return "draft";
      case 2:
        return "locked";
      case 3:
        return "active";
      case 4:
        return "paused";
      case 5:
        return "completed";
      case 6:
        return "deleted";
      case 0:
      default:
        return "draft";
    }
  }

  const normalizedStatus = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  switch (normalizedStatus) {
    case "inreview":
    case "review":
    case "validation":
    case "locked":
      return "locked";
    case "live":
    case "development":
    case "active":
      return "active";
    case "paused":
      return "paused";
    case "completed":
    case "launched":
      return "completed";
    case "deleted":
    case "archived":
    case "removed":
      return "deleted";
    case "concept":
    case "ideation":
    case "draft":
    default:
      return "draft";
  }
};

const getProductPresentation = (
  type: number | undefined,
  seed: number,
): Pick<ExperimentData, "product" | "productColor"> => {
  switch (type) {
    case 0:
      return { product: "Discovery", productColor: "#2AA9C2" };
    case 1:
      return { product: "Validation", productColor: "#5074FF" };
    case 2:
      return { product: "Optimization", productColor: "#E74895" };
    default:
      return {
        product: "Innovation",
        productColor:
          fallbackProductPalette[seed % fallbackProductPalette.length],
      };
  }
};

const getExperimentCode = (
  apiExperiment: ApiExperiment,
  index: number,
  seed: number,
) => {
  const idFragment = String(apiExperiment.id ?? "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 3)
    .toUpperCase();

  if (idFragment) {
    return `EXP-${idFragment}`;
  }

  return `EXP-${index + 1 + (seed % 9)}`;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const mapApiMetrics = (apiExperiment: ApiExperiment): ExperimentMetrics => {
  const participants = isFiniteNumber(apiExperiment.participantsCount)
    ? Math.max(0, Math.round(apiExperiment.participantsCount))
    : null;

  const completionRate = isFiniteNumber(apiExperiment.participantsCompletion)
    ? Math.min(
        100,
        Math.max(0, Math.round(apiExperiment.participantsCompletion)),
      )
    : null;

  const events24h = isFiniteNumber(apiExperiment.participantsEvents)
    ? Math.max(0, Math.round(apiExperiment.participantsEvents))
    : null;

  return {
    participants,
    completionRate,
    events24h,
    responses: participants,
    targetResponses: participants,
  };
};

const mapApiExperimentToDashboardExperiment = (
  apiExperiment: ApiExperiment,
  index: number,
): ExperimentData => {
  const createdAt = normalizeApiTimestamp(apiExperiment.createdAt);
  const updatedAt = normalizeApiTimestamp(apiExperiment.updatedAt);
  const name =
    apiExperiment.title ?? apiExperiment.name ?? "Untitled Experiment";
  const ownerName = apiExperiment.owner?.trim() || "Unassigned";
  const status = mapApiStatusToDashboardStatus(apiExperiment.status);
  const seed = hashString(`${apiExperiment.id ?? name}-${index}`);
  const productPresentation = getProductPresentation(apiExperiment.type, seed);
  const metrics = mapApiMetrics(apiExperiment);

  return {
    id: apiExperiment.id ?? `experiment-${index}`,
    code: getExperimentCode(apiExperiment, index, seed),
    name,
    product: productPresentation.product,
    productColor: productPresentation.productColor,
    status,
    owner: {
      name: ownerName,
      shortName: toShortName(ownerName),
      initials: toInitials(ownerName),
    },
    updatedLabel: formatRelativeTime(updatedAt ?? createdAt),
    updatedMinutesAgo: getRelativeMinutes(updatedAt ?? createdAt),
    metrics,
    highlight: false,
  };
};

export const getDashboardExperiments = async (): Promise<ExperimentData[]> => {
  let data: ExperimentsApiResponse | undefined;
  let lastError: unknown;

  for (const baseUrl of API_BASE_URL_CANDIDATES) {
    try {
      const response = await axios.get<ExperimentsApiResponse>(
        `${baseUrl}/api/v1/experiments`,
      );
      data = response.data;
      break;
    } catch (error) {
      try {
        const fallbackResponse = await axios.get<ExperimentsApiResponse>(
          `${baseUrl}/api/v1/experiments/organization/${FALLBACK_ORG_ID}/userid${FALLBACK_USER_ID}`,
        );
        data = fallbackResponse.data;
        break;
      } catch (fallbackError) {
        lastError = fallbackError ?? error;
      }
    }
  }

  if (!data) {
    throw lastError ?? new Error("Failed to load dashboard experiments");
  }

  const experiments = data.data?.experiments ?? [];
  return experiments.map(mapApiExperimentToDashboardExperiment);
};

export const useDashboardExperiments = () => {
  return useQuery({
    queryKey: DASHBOARD_QUERY_KEY,
    queryFn: getDashboardExperiments,
  });
};
